import { Typography } from "@cur8/maneki";
import { RefObject, useEffect, useRef, useState } from "react";
import {
  SeverityLevel,
  useAppInsights,
} from "render/context/AppInsightsContext";
import { decodeFrame } from "./decodeFrame";
import { warmUpDecoder } from "./warmUpDecoder";
import { applyCameraConstraints } from "./applyCameraConstraints";
import styles from "./styles.module.sass";

interface BarcodeScannerProps {
  onScan(barcodeId: string): void;
  /**
   * Surfaces all scanner-internal errors (canvas context, getUserMedia,
   * decode-loop, frame watchdog, applyConstraints is intentionally excluded).
   * Required so failures can't be silently swallowed by a forgetful caller.
   */
  onError(error: Error): void;
  active: boolean;
  /**
   * Incrementing counter — bumping it re-arms the scanner. Required when
   * `active` doesn't toggle between scans.
   */
  resetToken?: number;
  retryToken?: number;
  restartToken?: number;
}

const DECODE_INTERVAL_MS = 100; // ~10 Hz
const ROI_RATIO = 0.6; // centered square, 60% of shorter side
const VIDEO_WATCHDOG_MS = 5_000;
const SCAN_ACK_FLASH_MS = 150;
// After firing onScan, suppress the same decoded text for this window so a
// barcode left in view doesn't immediately re-scan once the consumer bumps
// resetToken (e.g. after a successful assignment).
const SAME_TEXT_DEDUPE_MS = 5_000;

export function BarcodeScanner({
  onScan,
  onError,
  active,
  resetToken,
  retryToken,
  restartToken,
}: BarcodeScannerProps) {
  const appInsights = useAppInsights();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);
  // Silences further onScan/onError callbacks until `resetToken` changes.
  // Set by: successful scan, decode-loop error, watchdog timeout, getUserMedia rejection.
  const silencedRef = useRef(false);
  // Last decoded text we fired onScan for, plus the rAF timestamp at the
  // moment of decode. Used to drop duplicate reads of the same barcode
  // within SAME_TEXT_DEDUPE_MS even across resetToken bumps.
  const lastScanRef = useRef<{ text: string; at: number } | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  onScanRef.current = onScan;
  onErrorRef.current = onError;

  useEffect(
    function resetSilenceOnReset() {
      silencedRef.current = false;
    },
    [resetToken]
  );

  useEffect(
    function clearScanMemoryOnRetry() {
      silencedRef.current = false;
      lastScanRef.current = null;
    },
    [retryToken]
  );

  // Kick off WASM download/instantiation as soon as the component mounts, so
  // it's likely ready by the time the camera-permission prompt is answered.
  // Failure is recoverable (next mount retries) so we trace as Warning, not
  // an exception.
  useEffect(
    function warmUpDecoderOnMount() {
      warmUpDecoder().catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        appInsights.trackTrace({
          message: `[BarcodeScanner] warmUpDecoder failed: ${message}`,
          severityLevel: SeverityLevel.Warning,
        });
      });
    },
    [appInsights]
  );

  useEffect(
    function acquireCameraStream() {
      if (!active) {
        return;
      }
      const video = videoRef.current;
      if (!video) {
        return;
      }

      // `cancelled` is the source of truth for "this effect run is over". The
      // async continuation must check it before touching state, since the effect
      // can be cleaned up while getUserMedia/play are inflight.
      let cancelled = false;
      let acquired: MediaStream | null = null;

      silencedRef.current = false;

      async function startCamera(videoEl: HTMLVideoElement) {
        try {
          acquired = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: "user" },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              frameRate: { ideal: 30, max: 30 },
            },
            audio: false,
          });
          if (cancelled) {
            stopAllTracks(acquired);
            return;
          }
          const track = acquired.getVideoTracks()[0];
          if (track) {
            await applyCameraConstraints(track);
          }
          videoEl.srcObject = acquired;
          await videoEl.play();
          if (cancelled) {
            stopAllTracks(acquired);
            return;
          }
          setStream(acquired);
        } catch (err) {
          if (cancelled) {
            return;
          }
          silencedRef.current = true;
          onErrorRef.current(
            err instanceof Error ? err : new Error(String(err))
          );
        }
      }

      startCamera(video);

      return () => {
        cancelled = true;
        stopAllTracks(acquired);
        setStream(null);
      };
    },
    // retryToken is deliberately not a dep: only restartToken re-acquires the
    // camera, so a plain dedupe-clear can't flicker a live stream.
    [active, restartToken]
  );

  useEffect(
    function runDecodeLoop() {
      if (!stream) {
        return;
      }
      const video = videoRef.current;
      if (!video) {
        return;
      }

      // Reuse the canvas across renders so we keep a single 2D context that
      // `willReadFrequently` has been hinted on.
      const canvas = (canvasRef.current ??= document.createElement("canvas"));
      if (!canvas.getContext("2d", { willReadFrequently: true })) {
        onErrorRef.current(new Error("Canvas 2D context unavailable"));
        return;
      }

      let stopped = false;
      let animationFrameId: number | null = null;
      let lastDecodeAt = 0;
      let watchdogId: number | null = null;
      // Guard against issuing a second decode while the previous one is still
      // resolving. zxing-wasm doesn't queue, so without this we'd fan out and
      // race onScan callbacks.
      let decodeInFlight = false;

      // Some devices return a stream that never emits frames (driver issue,
      // privacy curtain). Surface a readable error after 5s instead of hanging
      // on a black viewport. Cleared by the first ready frame in `tick`.
      watchdogId = window.setTimeout(() => {
        if (stopped || silencedRef.current) {
          return;
        }
        if (video.readyState < 2) {
          silencedRef.current = true;
          onErrorRef.current(
            new Error("Camera started but no frames arrived within 5s")
          );
        }
      }, VIDEO_WATCHDOG_MS);

      function tick(now: number) {
        if (stopped) {
          return;
        }
        // Always re-schedule first so an early return (decode in flight, video
        // not ready, throttled) doesn't permanently kill the loop.
        animationFrameId = requestAnimationFrame(tick);
        if (silencedRef.current) {
          return;
        }
        // Re-read refs each frame: function declarations are hoisted, so TS
        // loses the outer null-narrowing across this boundary. Reading here
        // also bails gracefully if the video element has unmounted.
        const video = videoRef.current;
        if (!video) {
          return;
        }
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) {
          return;
        }
        // HAVE_CURRENT_DATA — without this the watchdog can't tell "no frames
        // yet" from "no frames ever".
        if (video.readyState < 2) {
          return;
        }
        // First real frame arrived; the watchdog has done its job.
        if (watchdogId != null) {
          window.clearTimeout(watchdogId);
          watchdogId = null;
        }
        if (now - lastDecodeAt < DECODE_INTERVAL_MS) {
          return;
        }
        if (decodeInFlight) {
          return;
        }

        const imageData = extractCenteredRoi(video, canvas, ctx, ROI_RATIO);
        if (!imageData) {
          return;
        }

        lastDecodeAt = now;
        decodeInFlight = true;
        decodeFrame(imageData)
          .then((result) => {
            if (stopped || silencedRef.current) {
              return;
            }
            if (result) {
              const last = lastScanRef.current;
              if (
                last &&
                last.text === result.text &&
                now - last.at < SAME_TEXT_DEDUPE_MS
              ) {
                // Same code, still warm — keep the decoder armed so a new
                // code (or this one after the window) is picked up.
                return;
              }
              lastScanRef.current = { text: result.text, at: now };
              // Silence further callbacks until the parent bumps resetToken;
              // otherwise we'd fire onScan repeatedly while the same code is in
              // view.
              silencedRef.current = true;
              flashTargetAck(containerRef, SCAN_ACK_FLASH_MS);
              onScanRef.current(result.text);
            }
          })
          .catch((err) => {
            if (stopped || silencedRef.current) {
              return;
            }
            silencedRef.current = true;
            onErrorRef.current(
              err instanceof Error ? err : new Error(String(err))
            );
          })
          .finally(() => {
            decodeInFlight = false;
          });
      }

      animationFrameId = requestAnimationFrame(tick);

      return () => {
        stopped = true;
        if (animationFrameId != null) {
          cancelAnimationFrame(animationFrameId);
        }
        if (watchdogId != null) {
          window.clearTimeout(watchdogId);
        }
      };
    },
    [stream]
  );

  return (
    <div ref={containerRef} className={styles.scanner} data-active={active}>
      <div className={styles.viewport}>
        <video ref={videoRef} className={styles.video} playsInline muted />
        <div className={styles.overlay} aria-hidden="true">
          <div className={styles.target} />
          <div className={styles.hint}>
            <Typography variant="body-s">
              Align the square code inside the frame
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}

function stopAllTracks(stream: MediaStream | null) {
  stream?.getTracks().forEach((t) => t.stop());
}

// Brief data-attribute flash on the container; CSS picks it up to flash a
// success ring around the target on a successful decode.
function flashTargetAck(
  containerRef: RefObject<HTMLDivElement>,
  durationMs: number
) {
  const container = containerRef.current;
  if (!container) {
    return;
  }
  container.dataset.scanAck = "true";
  window.setTimeout(() => {
    if (containerRef.current === container) {
      delete container.dataset.scanAck;
    }
  }, durationMs);
}

// Crop a centred square ROI; the operator aligns the code into the overlay,
// so decoding the whole frame would just feed zxing noise.
function extractCenteredRoi(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  ratio: number
): ImageData | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) {
    return null;
  }
  const side = Math.floor(Math.min(vw, vh) * ratio);
  const sx = Math.floor((vw - side) / 2);
  const sy = Math.floor((vh - side) / 2);

  canvas.width = side;
  canvas.height = side;
  ctx.drawImage(video, sx, sy, side, side, 0, 0, side, side);
  return ctx.getImageData(0, 0, side, side);
}
